import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Eye, PlusCircle, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShops } from '../services/shopService';
import { deleteShopProduct, fetchShopProducts, updateShopProduct } from '../services/shopProductService';

const mapVariantForForm = (variant) => ({
  productVariantId: variant.productVariantId || variant.productVariant?.id || '',
  price: variant.price != null ? String(variant.price) : '0',
  stock: variant.stock != null ? String(variant.stock) : '0',
  isAvailable: variant.isAvailable !== false,
  name: variant.productVariant?.name || variant.name || '',
});

export default function ShopProducts() {
  const [shopId, setShopId] = useState('');
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    customName: '',
    customDescription: '',
    isAvailable: '1',
  });
  const [variantList, setVariantList] = useState([]);
  const [newVariant, setNewVariant] = useState({
    productVariantId: '',
    price: '',
    stock: '',
    isAvailable: '1',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    fetchShops({ limit: 200 })
      .then((res) => {
        const rec = res.data?.data?.records || res.data?.data || [];
        setShops(rec);
        if (rec.length) setShopId(rec[0].id);
      })
      .catch(() => toast.error('Failed to load shops'));
  }, []);

  useEffect(() => {
    if (shopId) loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, search, page, limit]);

  const loadProducts = async () => {
    try {
      const res = await fetchShopProducts(shopId, { search, page, limit });
      const rec = res.data?.data?.records || res.data?.data || [];
      const meta = res.data?.data?.meta || {};
      setProducts(rec);
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || rec.length || 0);
    } catch (error) {
      toast.error('Failed to load shop products');
    }
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setProductForm({
      customName: product.customName || '',
      customDescription: product.customDescription || '',
      isAvailable: product.isAvailable ? '1' : '0',
    });
    const mapped = Array.isArray(product.variants)
      ? product.variants.map(mapVariantForForm)
      : [];
    setVariantList(mapped);
    setNewVariant({ productVariantId: '', price: '', stock: '', isAvailable: '1' });
    setDrawerOpen(true);
  };

  const availableBaseVariants = useMemo(() => {
    if (!selectedProduct?.product?.variants) return [];
    const used = new Set(variantList.map((variant) => variant.productVariantId));
    return selectedProduct.product.variants.filter((variant) => !used.has(variant.id));
  }, [selectedProduct, variantList]);

  const updateVariantField = (index, field, value) => {
    setVariantList((prev) =>
      prev.map((variant, i) =>
        i === index
          ? {
              ...variant,
              [field]: field === 'isAvailable' ? value : value,
            }
          : variant,
      ),
    );
  };

  const removeVariant = (index) => {
    setVariantList((prev) => prev.filter((_, i) => i !== index));
  };

  const addVariant = () => {
    if (!newVariant.productVariantId) {
      toast.error('Select a variant to add');
      return;
    }
    const base = selectedProduct?.product?.variants?.find(
      (variant) => String(variant.id) === newVariant.productVariantId,
    );
    if (!base) {
      toast.error('Variant not found');
      return;
    }
    setVariantList((prev) => [
      ...prev,
      {
        productVariantId: base.id,
        price: newVariant.price || '0',
        stock: newVariant.stock || '0',
        isAvailable: newVariant.isAvailable === '1',
        name: base.name,
      },
    ]);
    setNewVariant({ productVariantId: '', price: '', stock: '', isAvailable: '1' });
  };

  const handleSave = async () => {
    if (!selectedProduct) return;
    if (!variantList.length) {
      toast.error('Add at least one variant');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        customName: productForm.customName,
        customDescription: productForm.customDescription,
        isAvailable: productForm.isAvailable === '1',
        variants: JSON.stringify(
          variantList.map((variant) => ({
            productVariantId: variant.productVariantId,
            price: Number(variant.price || 0),
            stock: Number(variant.stock || 0),
            isActive: variant.isAvailable !== false,
            isAvailable: variant.isAvailable !== false,
          })),
        ),
      };
      await updateShopProduct(shopId, selectedProduct.id, payload);
      toast.success('Shop product updated');
      setDrawerOpen(false);
      setSelectedProduct(null);
      loadProducts();
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!product || !shopId) return;
    const confirmation = window.confirm(
      'Are you sure you want to remove this product from the shop?'
    );
    if (!confirmation) return;

    try {
      setDeleting((prev) => ({ ...prev, [product.id]: true }));
      await deleteShopProduct(shopId, product.id);
      toast.success('Shop product removed');
      await loadProducts();
    } catch (error) {
      toast.error('Failed to delete shop product');
    } finally {
      setDeleting((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <select
            value={shopId}
            onChange={(e) => {
              setShopId(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {shops.map((shop) => (
              <option key={shop.id} value={shop.id}>
                {shop.name}
              </option>
            ))}
          </select>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {[10, 20, 50, 100].map((value) => (
              <option key={value} value={value}>
                {value} per page
              </option>
            ))}
          </select>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search"
            className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <Link
          to={`/shop-products/add?shopId=${shopId}`}
          className="flex items-center space-x-1 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-500"
        >
          <PlusCircle size={18} />
          <span>Add Shop Product</span>
        </Link>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th className="py-2 px-3">Product</th>
              <th className="py-2 px-3">Custom Name</th>
              <th className="py-2 px-3">Variants</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-b border-gray-200 dark:border-gray-700">
                <td className="py-2 px-3">{product.product?.name || '-'}</td>
                <td className="py-2 px-3">{product.customName || '-'}</td>
                <td className="py-2 px-3">{product.variants?.length || 0}</td>
                <td className="py-2 px-3">{product.isAvailable ? 'Available' : 'Unavailable'}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleView(product)}
                      className="inline-flex items-center space-x-1 text-indigo-600"
                    >
                      <Eye size={16} />
                      <span className="hidden sm:inline">View</span>
                    </button>
                    <Link
                      to={`/shop-products/add?shopId=${shopId}&shopProductId=${product.id}`}
                      className="inline-flex items-center space-x-1 text-gray-600"
                    >
                      <Edit size={16} />
                      <span className="hidden sm:inline">Edit</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(product)}
                      className="inline-flex items-center space-x-1 text-red-600 disabled:opacity-60"
                      disabled={Boolean(deleting[product.id])}
                    >
                      <Trash2 size={16} />
                      <span className="hidden sm:inline">
                        {deleting[product.id] ? 'Removing...' : 'Delete'}
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2 text-sm text-gray-600 dark:text-gray-300">
        <div>
          {totalRecords > 0 ? (
            <span>
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords}{' '}
              entries
            </span>
          ) : (
            <span>No products found</span>
          )}
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
          >
            Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages || totalPages === 0}
            className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
          >
            Next
          </button>
        </div>
      </div>

      {drawerOpen && selectedProduct && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 flex justify-end z-50">
          <div className="relative bg-white dark:bg-gray-900 dark:text-white w-full sm:w-[420px] h-full overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{selectedProduct.product?.name}</h2>
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Custom Name</label>
                <input
                  value={productForm.customName}
                  onChange={(e) => setProductForm({ ...productForm, customName: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-800 dark:border-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Custom Description</label>
                <textarea
                  value={productForm.customDescription}
                  onChange={(e) =>
                    setProductForm({ ...productForm, customDescription: e.target.value })
                  }
                  className="border px-3 py-2 rounded w-full dark:bg-gray-800 dark:border-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={productForm.isAvailable}
                  onChange={(e) => setProductForm({ ...productForm, isAvailable: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="1">Available</option>
                  <option value="0">Unavailable</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Variants</h3>
                </div>
                <div className="space-y-3">
                  {variantList.map((variant, index) => (
                    <div
                      key={`${variant.productVariantId}-${index}`}
                      className="border rounded p-3 bg-gray-50 dark:bg-gray-800"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{variant.name || 'Variant'}</span>
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="text-red-500 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs mb-1">Price</label>
                          <input
                            type="number"
                            value={variant.price}
                            min="0"
                            onChange={(e) => updateVariantField(index, 'price', e.target.value)}
                            className="border px-2 py-1 rounded w-full dark:bg-gray-900 dark:border-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Stock</label>
                          <input
                            type="number"
                            value={variant.stock}
                            min="0"
                            onChange={(e) => updateVariantField(index, 'stock', e.target.value)}
                            className="border px-2 py-1 rounded w-full dark:bg-gray-900 dark:border-gray-700"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs mb-1">Availability</label>
                          <select
                            value={variant.isAvailable ? '1' : '0'}
                            onChange={(e) =>
                              updateVariantField(index, 'isAvailable', e.target.value === '1')
                            }
                            className="border px-2 py-1 rounded w-full dark:bg-gray-900 dark:border-gray-700"
                          >
                            <option value="1">Available</option>
                            <option value="0">Unavailable</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border rounded p-3 bg-gray-50 dark:bg-gray-800">
                <h4 className="font-semibold mb-2 text-sm">Add Variant</h4>
                <div className="space-y-2">
                  <select
                    value={newVariant.productVariantId}
                    onChange={(e) => setNewVariant({ ...newVariant, productVariantId: e.target.value })}
                    className="border px-2 py-1 rounded w-full dark:bg-gray-900 dark:border-gray-700"
                  >
                    <option value="">Select variant</option>
                    {availableBaseVariants.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="Price"
                      value={newVariant.price}
                      onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
                      className="border px-2 py-1 rounded dark:bg-gray-900 dark:border-gray-700"
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Stock"
                      value={newVariant.stock}
                      onChange={(e) => setNewVariant({ ...newVariant, stock: e.target.value })}
                      className="border px-2 py-1 rounded dark:bg-gray-900 dark:border-gray-700"
                    />
                  </div>
                  <select
                    value={newVariant.isAvailable}
                    onChange={(e) => setNewVariant({ ...newVariant, isAvailable: e.target.value })}
                    className="border px-2 py-1 rounded w-full dark:bg-gray-900 dark:border-gray-700"
                  >
                    <option value="1">Available</option>
                    <option value="0">Unavailable</option>
                  </select>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="w-full bg-indigo-600 text-white py-1.5 rounded hover:bg-indigo-500"
                  >
                    Add Variant
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setDrawerOpen(false);
                    setSelectedProduct(null);
                  }}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

